import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import Link from 'ageas-react-component-link'

import { MatchedEntity } from '../../@types/PolicyHolderDetails'
import { NIF } from '../../Components/NIF'
import { CreateEntity } from '../../Components/OwnerData/CreateEntity'
import { SelectedEntity } from '../../Components/SelectedEntity'
import { usePolicyholderDetailsStore } from '../../Stores/policyholderDetailsStore'
import { useReportStore } from '../../Stores/reportStore'
import { useSimulatorStore } from '../../Stores/simulatorStore'
import { useCreatePolicyHolderEntityStore } from '../../Stores/useCreatePolicyholderEntityStore'
import { useCreateProposalStore } from '../../Stores/useCreateProposalStore'
import { useEntityDetailsForSummaryDocumentStore } from '../../Stores/useEntityDetailsForSummaryDocumentStore'
import { STATUS_REQUEST } from '../../constants/statusRequest'
import { useGetPolicyholderDetails } from '../../hooks/api/useGetPolicyholderDetails'
import { useValidateNif } from '../../hooks/api/useValidateNif'
import { useErrorRequest } from '../../hooks/useErrorRequest'
import { t } from '../../i18n'
import { validateNIF } from '../../utils/validates'
import { OwnerContainer } from './style'

type Props = {
  setLoading: (value: boolean) => void
}

const GLOBAL_ENTITY = 'GE'
const elementPortal = document.getElementsByTagName('body')?.[0]

export const Owner = ({ setLoading }: Props) => {
  const [pristineNif, setPristineNif] = useState(false)
  const [showCreateEntityButton, setShowCreateEntityButton] = useState(false)
  const [fiscalNumberMessage, setFiscalNumberMessage] = useState<string | null>(
    null,
  )
  const { isProspect, setIsProspect } = useReportStore()
  const { handleErrorRequest } = useErrorRequest()
  const { entitySelected, ownerFiscalNumber, setOwnerFiscalNumber } =
    useSimulatorStore()
  const { entityDetailsForSummaryDocumentSelected } =
    useEntityDetailsForSummaryDocumentStore()
  const {
    setPolicyholderDetails,
    setPolicyholderDetailsSelected,
    setPolicyholderAddressSelected,
  } = usePolicyholderDetailsStore()
  const { createPolicyHolderEntity, isPolicyholderCreated } =
    useCreatePolicyHolderEntityStore()
  const { createProposal } = useCreateProposalStore()
  const {
    mutate: mutateValidateNif,
    isSuccess: isSuccessValidateNif,
    isLoading: isLoadingValidateNif,
    isError: isErrorValidateNif,
    reset: resetValidateNif,
  } = useValidateNif()

  const initialVatNumber =
    ownerFiscalNumber ||
    entitySelected?.vatNumber ||
    entityDetailsForSummaryDocumentSelected?.vatNumber ||
    ''

  const [entityDetailGetRequest, setEntityDetailGetRequest] = useState(false)
  const [showSelectedEntity, setShowSelectedEntity] = useState(false)
  const [showCreateEntity, setShowCreateEntity] = useState(false)

  const { data, isFetching, isError, error } = useGetPolicyholderDetails(
    {
      vatNumber:
        ownerFiscalNumber ||
        createPolicyHolderEntity?.policyHolder?.fiscalNumber?.toString() ||
        null,
      entityType: 'individual',
      idExternalType: isPolicyholderCreated
        ? GLOBAL_ENTITY
        : createProposal?.createProposalResponse?.policyHolderData
            ?.idExternalType || '',
    },
    entityDetailGetRequest,
  )

  const entityDetails: MatchedEntity[] =
    data?.matchedIndividualEntities as never

  useEffect(() => {
    if (isPolicyholderCreated) {
      setEntityDetailGetRequest(isPolicyholderCreated)
    }
  }, [isPolicyholderCreated])

  useEffect(() => {
    setOwnerFiscalNumber(initialVatNumber)
  }, [initialVatNumber])

  useEffect(() => {
    if (!isProspect) {
      setEntityDetailGetRequest(Boolean(initialVatNumber))
    }
  }, [isProspect, initialVatNumber])

  useEffect(() => {
    if (ownerFiscalNumber.length) {
      setPristineNif(true)
    }
  }, [ownerFiscalNumber])

  useEffect(() => {
    const entityPreSelected = data?.matchedIndividualEntities?.find(
      (item) =>
        item.idEntity ===
        (entitySelected?.idEntity ||
          entityDetailsForSummaryDocumentSelected?.idEntity),
    )

    if (entityPreSelected?.name?.length) {
      setPolicyholderDetailsSelected(entityPreSelected!)
    } else if (data?.matchedIndividualEntities?.length === 1 && data?.referrals?.[0]?.outcomeTypeCode !== "S") {
      setPolicyholderDetailsSelected(data?.matchedIndividualEntities[0] )
      setPolicyholderAddressSelected(
        data?.matchedIndividualEntities?.[0]?.addresses?.[0]!,
      )
    }
  }, [
    data?.matchedIndividualEntities,
    entityDetailsForSummaryDocumentSelected?.name,
    entitySelected?.name,
    setPolicyholderDetailsSelected,
  ])

  useEffect(() => {
    if (data && data?.referrals?.[0]?.outcomeTypeCode !== "S") {
      setPolicyholderDetails(data!)
    }
  }, [data, setPolicyholderDetails])

  useEffect(() => {
    setLoading(isFetching || isLoadingValidateNif)
  }, [isFetching, setLoading, isLoadingValidateNif])

  useEffect(() => {
    if (pristineNif) {
      setEntityDetailGetRequest(isFetching)
    }
  }, [isFetching, pristineNif])

  useEffect(() => {
    const _error: any = error

    if (isError && _error) {
      if (_error?.response?.status === STATUS_REQUEST.notFound) {
        setFiscalNumberMessage(t('pages.step1.errors.notFoundEntity'))
        setShowCreateEntityButton(true)
        return
      }

      if (
        Array.isArray(_error?.response?.data) &&
        (_error?.response?.data || [])?.filter(
          (err: any) => err?.errorCode === 'ERC362',
        ).length
      ) {
        setFiscalNumberMessage(t('general.errors.fiscalNumberInvalid'))

        return
      }

      handleErrorRequest({
        error,
      })
    }
  }, [error, handleErrorRequest, isError])

  useEffect(() => {
    if (data && entityDetailGetRequest) {
      if ((data?.matchedIndividualEntities || []).length > 0) {
        if (data?.referrals?.[0]?.outcomeTypeCode === "S") { 
            setFiscalNumberMessage(data?.referrals?.[0]?.outcomeMessage ?? "")
        }
        else if(
          (data.matchedIndividualEntities || [])?.some(
            (item) => item.isProspect,
          )
        ) {
          setIsProspect(true)
          setShowCreateEntityButton(true)
        } else {
          setShowSelectedEntity(true)
        }
      } else if (
        (data?.matchedIndividualEntities || []).length === 0 &&
        pristineNif
      ) {
        setFiscalNumberMessage(t('pages.step1.errors.notFoundEntity'))
        setShowCreateEntityButton(true)
      }
    }
  }, [data?.matchedIndividualEntities, pristineNif, entityDetailGetRequest])

  useEffect(() => {
    if (isSuccessValidateNif) {
      setShowCreateEntity(true)
    }
  }, [isSuccessValidateNif])

  useEffect(() => {
    if (isErrorValidateNif) {
      setOwnerFiscalNumber('')
      setPristineNif(false)
      setFiscalNumberMessage(null)
      setShowCreateEntityButton(true)

      const nifRefInput: any = document?.querySelector(`#nifRefInput input`)
      nifRefInput!.value = ''
    }
  }, [isErrorValidateNif])

  return (
    <>
      <OwnerContainer>
        <div className="row">
          {!showCreateEntity && !isLoadingValidateNif && (
            <div id="nifRefInput">
              <NIF
                readOnly={isProspect && !isErrorValidateNif}
                label={t('pages.step3.owner.NIF')}
                value={ownerFiscalNumber}
                errorMessage={fiscalNumberMessage}
                onValueChanged={(value: any) => {
                  if ((value || '').length > 0) {
                    setPristineNif(true)
                    if (validateNIF(value)) {
                      setFiscalNumberMessage(t('general.search'))
                      setOwnerFiscalNumber(value)
                    } else {
                      setEntityDetailGetRequest(false)
                      setFiscalNumberMessage(
                        t('general.errors.fiscalNumberInvalid'),
                      )
                    }
                  } else {
                    setFiscalNumberMessage('')
                  }
                }}
                onClick={() => {
                  if (validateNIF(ownerFiscalNumber)) {
                    setFiscalNumberMessage(null)
                    setEntityDetailGetRequest(true)
                    resetValidateNif()
                  } else {
                    setFiscalNumberMessage(
                      t('general.errors.fiscalNumberInvalid'),
                    )
                    setEntityDetailGetRequest(false)
                  }
                }}
              />
            </div>
          )}
        </div>
        {!isLoadingValidateNif && !isErrorValidateNif && (
          <>
            {((showCreateEntityButton &&
              (data?.matchedIndividualEntities || []).length === 0) ||
              isProspect) && (
              <div className="button-create">
                {!isFetching && (
                  <Link
                    label={t('pages.step3.owner.createEntity')}
                    onValueChanged={() =>
                      mutateValidateNif({ vatNumber: ownerFiscalNumber })
                    }
                  />
                )}
              </div>
            )}
          </>
        )}
        {(data?.matchedIndividualEntities || []).length > 0 && !isProspect && data?.referrals?.[0]?.outcomeTypeCode !== "S"&&(
          <div className="button-create">
            <Link
              label={t('pages.step3.owner.selectedEntity')}
              onValueChanged={() => setShowSelectedEntity(true)}
            />
          </div>
        )}
      </OwnerContainer>
      {createPortal(
        <>
          <SelectedEntity
            isVisible={showSelectedEntity}
            type="policyHolder"
            data={entityDetails}
            closeAction={() => setShowSelectedEntity(false)}
          />
        </>,
        elementPortal!,
      )}
      {createPortal(
        <>
          <CreateEntity
            isVisible={showCreateEntity}
            closeAction={() => setShowCreateEntity(false)}
          />
        </>,
        elementPortal!,
      )}
    </>
  )
}
